import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { format, parseISO, set } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { CalendarIcon, Clock } from 'lucide-react'
import { HealthCardReader } from '@/components/HealthCardReader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useExaminationRoomAppointmentSetting } from '@/hooks/useAppointmentSetting'
import { Appointment } from '@/hooks/useAppointments'
import { useAppointmentLastDataByExaminationRoomId } from '@/hooks/useAppointmentLastDataByExaminationRoomId'
import { cn } from '@/lib/utils'
import { useViewMode } from '@/stores/viewMode'
import { toast } from 'sonner'

// 表單欄位型別
type FormValues = {
  patientName: string
  patientHealthId: string
  patientPhone?: string
  patientEmail?: string
  appointmentDate: Date
  appointmentTime?: Date
  sequenceNumber?: number
  appointmentSource: 'ONLINE' | 'WALK_IN' | 'PHONE'
  appointmentStatus: 'SCHEDULED' | 'CHECKED_IN' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW'
  notes?: string
}

// 提交時送出的資料型別（日期/時間已格式化為字串，並補上診所與診間資訊）
export type AppointmentFormSubmitData = Omit<FormValues, 'appointmentDate' | 'appointmentTime'> & {
  appointmentDate: string
  appointmentTime?: string
  clinicId?: number | null
  examinationRoomId?: number
}

type AppointmentFormProps = {
  examinationRoomId: number
  appointment?: Appointment
  onSubmit: (data: AppointmentFormSubmitData) => void
  onCancel: () => void
}

export function AppointmentForm({
  examinationRoomId,
  appointment,
  onSubmit,
  onCancel,
}: AppointmentFormProps) {
  const { id: clinicId } = useViewMode()
  const [selectedTime, setSelectedTime] = useState<Date | undefined>(
    appointment?.appointment_time ? parseISO(appointment.appointment_time) : undefined,
  )

  // 獲取診間的預約掛號設定
  const { appointmentSettings } = useExaminationRoomAppointmentSetting(examinationRoomId)
  const settingForMode = appointmentSettings?.[0]
  const isSharedMode = settingForMode?.appointment_mode === 'SHARED_ROOMS'
  const sharedRooms = settingForMode?.examination_rooms ?? []

  // SHARED 模式下的派發診間（'' = 待派發）。編輯時用既有 examination_room_id，建立時預設待派發。
  const [dispatchRoomId, setDispatchRoomId] = useState<string>(
    appointment?.examination_room_id ? String(appointment.examination_room_id) : '',
  )

  // appointment prop 變動時同步 dispatchRoomId（防止 dialog 重用造成的 stale state）
  useEffect(() => {
    setDispatchRoomId(
      appointment?.examination_room_id ? String(appointment.examination_room_id) : '',
    )
  }, [appointment?.appointment_id, appointment?.examination_room_id])

  // 獲取選擇日期的最新預約資料
  // 此 state 僅用於觸發日期變更時的副作用，值本身不會被讀取
  const [, setSelectedDate] = useState<string>(
    appointment?.appointment_date || new Date().toISOString().split('T')[0], // 預設為今天或編輯中的預約日期
  )

  const { appointment: lastAppointment } =
    useAppointmentLastDataByExaminationRoomId(examinationRoomId)

  const appointmentSetting =
    appointmentSettings && appointmentSettings.length > 0 ? appointmentSettings[0] : null

  // 計算預設的序號
  const calculateDefaultSequenceNumber = () => {
    let sequenceNumber

    // 如果是編輯現有預約，使用該預約的序號
    if (appointment?.sequence_number !== undefined) {
      sequenceNumber = appointment.sequence_number
    }
    // 如果有最新的預約資料，則使用該值的 sequence_number + 1 或 1
    else if (lastAppointment) {
      sequenceNumber =
        lastAppointment.sequence_number !== null && lastAppointment.sequence_number !== undefined
          ? lastAppointment.sequence_number + 1
          : 1
    }
    // 如果沒有最新的預約資料，預設為 1
    else {
      sequenceNumber = 1
    }

    // 如果尾數是8，自動加1
    if (sequenceNumber % 10 === 8) {
      sequenceNumber += 1
    }

    return sequenceNumber
  }

  // 初始化表單
  const form = useForm<FormValues>({
    // resolver: zodResolver(formSchema as any),
    defaultValues: {
      patientName: appointment?.patient_name || '',
      patientHealthId: appointment?.patient_health_id || '',
      patientPhone: appointment?.patient_phone || '',
      patientEmail: appointment?.patient_email || '',
      appointmentDate: appointment?.appointment_date
        ? parseISO(appointment.appointment_date)
        : new Date(), // 確保有預設日期值
      appointmentTime: appointment?.appointment_time
        ? parseISO(appointment.appointment_time)
        : undefined,
      sequenceNumber: calculateDefaultSequenceNumber(),
      appointmentSource: appointment?.appointment_source || 'WALK_IN', // 預設為現場掛號
      appointmentStatus: appointment?.appointment_status || 'CHECKED_IN',
      notes: appointment?.notes || '',
    },
    mode: 'onChange', // 啟用即時驗證
  })

  // 當預約掛號設定載入完成後，設置預約類型（只在初始化時設置一次）
  useEffect(() => {
    if (appointmentSetting && !appointment) {
      const currentSequenceNumber = form.getValues('sequenceNumber')

      // 只有在序號為空或為預設值時才設置，避免覆蓋用戶修改的值
      if (
        currentSequenceNumber === undefined ||
        currentSequenceNumber === 0 ||
        currentSequenceNumber === 1
      ) {
        // 如果是新增掛號(預約)且有預約掛號設定，則根據設定初始化表單
        if (appointmentSetting.appointment_type === 'SEQUENTIAL') {
          // 如果是流水號預約，設置默認序號
          const metadata = appointmentSetting.metadata || {}
          const startNumber = metadata.start_number || 1

          // 根據預約掛號設定的流水號類型計算默認序號
          if (metadata.sequential_type === 'ODD_EVEN') {
            // 如果是奇偶數流水號，根據掛號來源設置默認序號
            const appointmentSource = form.getValues('appointmentSource')

            if (appointmentSource === 'ONLINE' && metadata.online_even) {
              // 線上預約使用偶數，計算下一個可用的偶數
              const nextEven = startNumber % 2 === 0 ? startNumber : startNumber + 1
              form.setValue('sequenceNumber', nextEven)
            } else if (appointmentSource === 'WALK_IN' && metadata.walk_in_odd) {
              // 現場掛號使用奇數，計算下一個可用的奇數
              const nextOdd = startNumber % 2 === 1 ? startNumber : startNumber + 1
              form.setValue('sequenceNumber', nextOdd)
            } else {
              // 其他情況使用默認起始號碼
              form.setValue('sequenceNumber', startNumber)
            }
          } else {
            // 一般流水號，直接使用起始號碼
            form.setValue('sequenceNumber', startNumber)
          }
        }
      }
    }
  }, [appointmentSetting, appointment, form])

  // 確保表單日期欄位在組件掛載時正確初始化
  useEffect(() => {
    // 確保 appointmentDate 欄位有值
    const currentDate = form.getValues('appointmentDate')
    if (!currentDate) {
      form.setValue('appointmentDate', new Date())
      form.trigger('appointmentDate') // 觸發驗證
    }
    // form 為 react-hook-form 的穩定參考，加入後仍只在掛載時執行一次
  }, [form])

  // 當最新預約資料變更時，更新序號（但不覆蓋用戶已經修改的值）
  useEffect(() => {
    // 如果不是編輯模式，才需要動態更新序號
    if (!appointment?.sequence_number) {
      const currentSequenceNumber = form.getValues('sequenceNumber')
      const newSequenceNumber = calculateDefaultSequenceNumber()

      // 只有當前序號與計算出的預設序號相同時，才更新（避免覆蓋用戶手動修改的值）
      if (
        currentSequenceNumber === undefined ||
        currentSequenceNumber === 0 ||
        currentSequenceNumber ===
          (lastAppointment?.sequence_number ? lastAppointment.sequence_number : 0)
      ) {
        form.setValue('sequenceNumber', newSequenceNumber)
      }
    }
    // calculateDefaultSequenceNumber 每次 render 都會重建，且僅依賴 appointment / lastAppointment（已列入相依），
    // 直接加入會造成每次 render 都觸發 setValue 而產生迴圈，故刻意省略
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAppointment, form, appointment])

  // 監聽表單日期變更，更新selectedDate以重新獲取該日期的最新預約資料
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      // 當預約日期變更時，更新 selectedDate
      if (name === 'appointmentDate' && value.appointmentDate) {
        const formattedDate = format(value.appointmentDate as Date, 'yyyy-MM-dd')
        setSelectedDate(formattedDate)
      }
    })

    return () => subscription.unsubscribe()
  }, [form])

  // 處理表單提交
  const handleSubmit = (values: FormValues) => {
    // 派發診間決定：
    // - SHARED 模式：用 dispatchRoomId（'' 代表待派發 → 傳 undefined）
    // - DEFAULT 模式：強制用 examinationRoomId（本診間）
    const resolvedRoomId = isSharedMode
      ? dispatchRoomId
        ? Number(dispatchRoomId)
        : undefined
      : examinationRoomId

    // 準備提交數據
    const submitData = {
      ...values,
      appointmentDate: format(values.appointmentDate, 'yyyy-MM-dd'),
      appointmentTime: values.appointmentTime
        ? format(values.appointmentTime, "yyyy-MM-dd'T'HH:mm:ss")
        : undefined,
      clinicId: clinicId,
      examinationRoomId: resolvedRoomId,
    }

    // 直接提交數據
    onSubmit(submitData)
  }

  // 處理時間選擇
  const handleTimeSelect = (hours: number, minutes: number) => {
    const date = form.getValues('appointmentDate') || new Date()
    const newTime = set(date, { hours, minutes, seconds: 0 })
    setSelectedTime(newTime)
    form.setValue('appointmentTime', newTime)
  }

  // 生成時間選項
  const generateTimeOptions = () => {
    const options = []
    const startTime = appointmentSetting?.day_start_time || '09:00'
    const endTime = appointmentSetting?.day_end_time || '17:00'

    const [startHour, startMinute] = startTime.split(':').map(Number)
    const [endHour, endMinute] = endTime.split(':').map(Number)

    const startMinutes = startHour * 60 + startMinute
    const endMinutes = endHour * 60 + endMinute

    // 時間間隔（分鐘）
    const interval = appointmentSetting?.metadata?.slot_duration_minutes || 30

    // 確保時間間隔大於 0，避免無限循環
    const safeInterval = interval > 0 ? interval : 30

    for (let minutes = startMinutes; minutes <= endMinutes; minutes += safeInterval) {
      const hour = Math.floor(minutes / 60)
      const minute = minutes % 60
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      options.push({ value: timeString, label: timeString, hour, minute })
    }

    return options
  }

  const timeOptions = generateTimeOptions()

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* SHARED 模式下的派發診間選擇 */}
        {isSharedMode && (
          <div className="space-y-2 rounded-md border p-4">
            <label className="text-sm font-medium">指定看診診間</label>
            <Select
              value={dispatchRoomId || 'PENDING'}
              onValueChange={(val) => setDispatchRoomId(val === 'PENDING' ? '' : val)}
            >
              <SelectTrigger>
                <SelectValue placeholder="稍後指定" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">稍後指定</SelectItem>
                {sharedRooms.map((r) => (
                  <SelectItem key={r.examination_room_id} value={String(r.examination_room_id)}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* 健保卡讀卡器 */}
        <div className="flex justify-end">
          <HealthCardReader
            onSuccess={(data) => {
              // 填入健保卡資料到表單
              form.setValue('patientName', data.full_name)
              form.setValue('patientHealthId', data.card_no)
              // 觸發表單驗證
              form.trigger(['patientName', 'patientHealthId'])
            }}
            onFailed={() => {
              toast.error('讀取失敗')
            }}
            className="mb-4"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 病患姓名 */}
          <FormField
            control={form.control}
            name="patientName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>病患姓名 *</FormLabel>
                <FormControl>
                  <Input placeholder="請輸入病患姓名" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* 健保卡號 */}
          <FormField
            control={form.control}
            name="patientHealthId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>健保卡號 *</FormLabel>
                <FormControl>
                  <Input placeholder="請輸入健保卡號" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* 聯絡電話 */}
          <FormField
            control={form.control}
            name="patientPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>聯絡電話</FormLabel>
                <FormControl>
                  <Input placeholder="請輸入聯絡電話" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* 電子郵件 */}
          <FormField
            control={form.control}
            name="patientEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>電子郵件</FormLabel>
                <FormControl>
                  <Input placeholder="請輸入電子郵件" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* 預約日期 */}
          <FormField
            control={form.control}
            name="appointmentDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>預約日期 *</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={'outline'}
                        className={cn(
                          'w-full pl-3 text-left font-normal',
                          !field.value && 'text-muted-foreground',
                        )}
                      >
                        {field.value ? (
                          format(field.value, 'yyyy/MM/dd', { locale: zhTW })
                        ) : (
                          <span>請選擇日期</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        if (date) {
                          field.onChange(date)
                          // 更新 selectedDate 以重新獲取該日期的最新預約資料
                          const formattedDate = format(date, 'yyyy-MM-dd')
                          setSelectedDate(formattedDate)
                        }
                      }}
                      disabled={(date) => date < new Date()}
                      locale={zhTW}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* 預約掛號時間或序號 */}
          {appointmentSetting?.appointment_type === 'TIME_BASED' ? (
            <FormField
              control={form.control}
              name="appointmentTime"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>預約掛號時間</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={'outline'}
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground',
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'HH:mm', { locale: zhTW })
                          ) : (
                            <span>請選擇時間</span>
                          )}
                          <Clock className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="start">
                      <div className="grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
                        {timeOptions.map((option) => (
                          <Button
                            key={option.value}
                            variant={
                              selectedTime && format(selectedTime, 'HH:mm') === option.value
                                ? 'default'
                                : 'outline'
                            }
                            className="text-center"
                            onClick={() => handleTimeSelect(option.hour, option.minute)}
                            type="button"
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <FormField
              control={form.control}
              name="sequenceNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>預約序號</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="請輸入預約序號"
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value ? parseInt(e.target.value) : undefined)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* 掛號來源 */}
          <FormField
            control={form.control}
            name="appointmentSource"
            render={({ field }) => (
              <FormItem>
                <FormLabel>掛號來源</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue="WALK_IN"
                  value="WALK_IN"
                  disabled={true}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue>現場掛號</SelectValue>
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="WALK_IN">現場掛號</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* 掛號狀態 */}
          <FormField
            control={form.control}
            name="appointmentStatus"
            render={({ field }) => (
              <FormItem>
                <FormLabel>掛號狀態</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="請選擇掛號狀態" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="SCHEDULED">已排程</SelectItem>
                    <SelectItem value="CHECKED_IN">已報到</SelectItem>
                    <SelectItem value="IN_PROGRESS">看診中</SelectItem>
                    <SelectItem value="COMPLETED">已完成</SelectItem>
                    <SelectItem value="CANCELLED">已取消</SelectItem>
                    <SelectItem value="NO_SHOW">未出席</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* 備註 */}
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>備註</FormLabel>
              <FormControl>
                <Textarea placeholder="請輸入備註" className="resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* 表單按鈕 */}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            取消
          </Button>
          <Button type="submit">{appointment ? '更新預約' : '建立預約'}</Button>
        </div>
      </form>
    </Form>
  )
}
